import React, { useState } from 'react';
import { Alert, Form, Button, Card, FilterGrid } from '@/components/ui';
import reportService from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import Toast from '../../../components/Toast';
import { ServiceArea } from '../../../services/areasService';

interface PreliminaryJobCostReportViewProps {
  serviceAreas: ServiceArea[];
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const PreliminaryJobCostReportView: React.FC<PreliminaryJobCostReportViewProps> = ({
  serviceAreas,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    startDate: '',
    endDate: ''
  });
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => {}
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

  // Server-side pagination helper
  const handlePageChange = (page: number, pageSize?: number) => {
    handleGeneratePreview(page, pageSize);
  };

  // Handle page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    handleGeneratePreview(1, newPageSize);
  };

  const handleGeneratePreview = async (page: number = 1, pageSize?: number) => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const response = await reportService.getPreliminaryJobCostPreview({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        page,
        limit: pageSize || pagination.limit,
        sortField,
        sortDirection
      });
      // Store only current page data (server-side pagination)
      setPreviewData(response.data);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
        onPageChange: handlePageChange
      });
    } catch (e) {
      setErrorMessage('Failed to generate job cost report preview.');
      setPreviewData([]);
      setPagination({ page: 1, limit: 50, total: 0, totalPages: 0, onPageChange: handlePageChange });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSort = (field: string) => {
    const newDirection = field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(newDirection);
    handleGeneratePreview(1); // Reset to page 1, fetch from server
  };

  const handleExport = async () => {
    setLoadingExport(true);
    try {
      await reportService.generatePreliminaryJobCostReport({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate
      });
      setToastMessage('Preliminary Job Cost report exported successfully!');
      setShowToast(true);
    } catch (e) {
      setErrorMessage('Failed to export job cost report.');
    } finally {
      setLoadingExport(false);
    }
  };

  const columns = [
    { label: 'Job', key: 'job', sortable: true, width: '160px' },
    { label: 'Activity', key: 'activity', sortable: true, width: '150px' },
    { label: 'Finance Class', key: 'financeClass', sortable: true, width: '150px' },
    { label: 'Date', key: 'date', sortable: true, width: '130px' },
    { label: 'Description', key: 'description', sortable: false, width: '220px' },
    { label: 'Amount', key: 'amount', sortable: true, width: '120px', render: (row: any) => (Number(row.amount) || 0).toFixed(2) }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}

        <ReportParametersCard
          onGenerate={() => handleGeneratePreview(1)}
          onExport={() => {}}
          loadingGenerate={loadingPreview}
          loadingExport={false}
          disableExport={true}
          hideExportButton={true}
          generateButtonText="Run Report"
          defaultCollapsed={false}
          onCollapseChange={setIsFiltersCollapsed}
        >
          <FilterGrid>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
              <Form.Select
                size="sm"
                value={filters.serviceArea}
                onChange={(e) => setFilters({ ...filters, serviceArea: e.target.value })}
                disabled={loadingServiceAreas}
              >
                <option value="">All Service Areas</option>
                {serviceAreas.map((area) => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Date Range</Form.Label>
              <div className="tw-flex tw-items-center tw-gap-2">
                <Form.Control
                  size="sm"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  style={{ width: '140px' }}
                />
                <span className="tw-text-content-muted tw-text-sm">to</span>
                <Form.Control
                  size="sm"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  style={{ width: '140px' }}
                />
              </div>
            </Form.Group>
          </FilterGrid>
        </ReportParametersCard>
      </div>

      {/* Disclaimer */}
      <div className="tw-mt-2 tw-mb-2">
        <Alert variant="warning" className="tw-py-2 tw-px-3 tw-mb-0">
          Preliminary amounts do not include burden.
        </Alert>
      </div>


      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
        <Card style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minWidth: 0 }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
            <h6 className="tw-mb-0">Report Data ({pagination.total} records)</h6>
            {pagination.total > 0 && (
              <Button variant="outline-primary" size="sm" onClick={handleExport}>
                <i className="bi bi-download tw-mr-1"></i>
                Export CSV
              </Button>
            )}
          </Card.Header>
          {/* ReportPreviewTable owns its own scroll region (single mechanism, see
              ReportPreviewTable.tsx); no extra overflow wrapper needed here. */}
          <Card.Body className="tw-p-0" style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <ReportPreviewTable
                columns={columns}
                data={previewData}
                pagination={{
                  ...pagination,
                  onPageChange: handlePageChange,
                  onPageSizeChange: handlePageSizeChange
                }}
                loading={loadingPreview}
                emptyMessage="No data available. Click 'Run Report' to load data."
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                isFiltersCollapsed={isFiltersCollapsed}
              />
          </Card.Body>
        </Card>
      </div>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type="success"
      />
    </div>
  );
};

export default PreliminaryJobCostReportView;


